import React, { useCallback, Fragment } from 'react';
import Link from 'next/link';

import { Formik, Form } from 'formik';
import { Block } from '@views/components/block';
import FormCheck from '@views/components/form/FormCheck';
import { Table } from '@views/components/cart';
import { Product as CartProduct } from '@views/components/cart';
import { Product } from '@views/components/product/Product';

import {
  Card,
  Row,
  Col,
  FormGroup,
  FormControl,
  FormLabel,
  Button,
  ListGroup,
  ListGroupItem,
  Badge,
} from 'react-bootstrap';
import useGoogleRecaptcha from '@views/modules/header/sub-modules/top/hooks/use-google-re-captcha';
import useOrderSearch from './hooks/user-order-search';
import styled from 'styled-components';
import LazyImage from '@views/components/lazy-image';

import { ORDER_STATUS } from '@views/modules/order-detail/intl';
import { ORDER_STATUS_COLORS } from '@views/modules/order-detail/const';
import _formatDate from '@helpers/format-date';
import { formatCurrencyVN as formatCurrency } from '@helpers/format-currency';
import { GIFT } from '@helpers/const';
import Skeleton from '@views/components/common/Skeleton';

import { INTERNAL_URI, productHref } from '@helpers/route';
import _isContactPrice from '@helpers/is-contact-price';

const CardForm = styled(Card)`
  border: 0;
  background: var(--pale-lilac);
`;

const CardImage = styled.a`
  flex: 0 0 50px;
  width: 50px;
  height: 50px;
`;

const TableContainer = styled(Table)`
  margin-left: -1.25rem;
  margin-right: -1.25rem;
  width: calc(100% + 2.5rem);
`;

const OrderStatus = styled(Badge)`
  background-color: ${(props) => props.color};
`;

enum SearchType {
  Phone = '1',
  Email = '2',
}

const OrderChecking = () => {
  let initialValues = {
    type: SearchType.Phone,
    value: '',
  };
  const { result, loading, search } = useOrderSearch(null);

  const [recaptcha, getTokenAsync] = useGoogleRecaptcha();
  const onSubmit = useCallback(async (values) => {
    const reCaptchaToken = await getTokenAsync();

    search({
      variables: {
        type: parseInt(values.type),
        value: values.value,
        recaptchaResponse: reCaptchaToken,
      },
    });
  }, []);

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      {({
        values,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue,
      }) => (
        <Block as={Form} onSubmit={handleSubmit}>
          <Row>
            <Col md={4}>
              <CardForm>
                <Card.Body>
                  <Card.Title>Kiểm tra đơn hàng của bạn</Card.Title>
                  <FormGroup>
                    <FormLabel>Phương thức kiểm tra</FormLabel>
                    <FormCheck disabled={isSubmitting}>
                      <input
                        className="form-type-check__input"
                        type="radio"
                        name="type"
                        id="orderCheckingPhone"
                        value={SearchType.Phone}
                        onChange={(event: React.ChangeEvent) => {
                          handleChange(event);
                          setFieldValue('value', '');
                        }}
                        checked={values.type === SearchType.Phone}
                        disabled={isSubmitting}
                      />
                      <label
                        className="form-type-check__label"
                        htmlFor="orderCheckingPhone"
                      >
                        Số điện thoại
                      </label>
                    </FormCheck>

                    <FormCheck disabled={isSubmitting}>
                      <input
                        className="form-type-check__input"
                        type="radio"
                        name="type"
                        id="orderCheckingEmail"
                        value={SearchType.Email}
                        onChange={(event: React.ChangeEvent) => {
                          handleChange(event);
                          setFieldValue('value', '');
                        }}
                        checked={values.type === SearchType.Email}
                        disabled={isSubmitting}
                      />
                      <label
                        className="form-type-check__label"
                        htmlFor="orderCheckingEmail"
                      >
                        Email
                      </label>
                    </FormCheck>
                  </FormGroup>

                  <FormGroup>
                    <FormLabel>
                      {values.type === SearchType.Email
                        ? 'Email'
                        : 'Số điện thoại'}
                    </FormLabel>
                    <FormControl
                      type={values.type === SearchType.Email ? 'email' : 'text'}
                      placeholder={
                        values.type === SearchType.Email
                          ? 'email@gmail.com'
                          : '0909 xxx xxx'
                      }
                      name="value"
                      value={values.value}
                      onChange={handleChange}
                      disabled={isSubmitting}
                    />
                  </FormGroup>
                  <FormGroup>
                    <div className="d-flex justify-content-center">
                      {recaptcha}
                    </div>
                  </FormGroup>
                  <FormGroup>
                    <Card.Text>
                      Nếu quý khách có bất kỳ thắc mắc nào, xin vui lòng gọi
                      Hotline: <b>0707467697</b>
                    </Card.Text>
                  </FormGroup>
                  <div className="text-right">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={isSubmitting}
                    >
                      Xem ngay
                    </Button>
                  </div>
                </Card.Body>
              </CardForm>
            </Col>

            <Col md={8}>
              {loading && <EmptyState />}

              <OrderList result={result} />
            </Col>
          </Row>
        </Block>
      )}
    </Formik>
  );
};

const EmptyState = () => (
  <Fragment>
    <ListGroup>
      {Array.from({ length: 5 }, (_, i) => Symbol(i).toString()).map((item) => (
        <ListGroupItem key={item}>
          <h4>
            <Skeleton
              width="30%"
              widthRandomness={0}
              height="25px"
              heightRandomness={0}
            />
          </h4>
          <p>
            <Skeleton
              count={2}
              width="100%"
              height="15px"
              heightRandomness={0}
            />
          </p>
        </ListGroupItem>
      ))}
    </ListGroup>
  </Fragment>
);

const OrderList = ({ result }) => (
  <Fragment>
    {result.map((item: any) => (
      <ListGroupItem key={item.get('id')}>
        <div>
          <h5>
            <span>Mã đơn hàng:</span>{' '}
            <span className="text-info font-weight">#{item.get('id')}</span>
          </h5>
          <p>
            <span>Ngày mua</span>:&nbsp;
            <span>{_formatDate(+item.get('createdAt'), 'dd/MM/yyyy')}</span>
            <br />
            <span>Tình trạng đơn hàng:</span> &nbsp;
            <OrderStatus
              color={ORDER_STATUS_COLORS[`${item.get('orderStatus')}`]}
            >
              {ORDER_STATUS[`${item.get('orderStatus')}`]}
            </OrderStatus>{' '}
            <br />
            <span>Họ tên:</span>{' '}
            <span>{item.getIn(['shippingInfo', 'name'])}</span> <br />
            <span>Địa chỉ giao hàng:</span>{' '}
            <span>{item.getIn(['shippingInfo', 'address'])}</span>
          </p>
        </div>
        <TableContainer>
          <Table.Head>
            <Table.Row>
              <Table.Col as="th" heading product>
                Sản phẩm
              </Table.Col>
              <Table.Col as="th" heading price>
                Giá
              </Table.Col>
              <Table.Col as="th" heading quantity>
                Số lượng
              </Table.Col>
              <Table.Col as="th" heading total>
                Thành tiền
              </Table.Col>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {item.get('products').size &&
              item.get('products').map((itemProduct: any) => {
                const productUri = productHref(
                  itemProduct.get('id'),
                  itemProduct.get('productName')
                );

                const price = itemProduct.get('typePrice') || 0;
                // const isContact = _isContactPrice(price);
                const isContact = item.get('isContactProduct');

                return (
                  <Table.Row key={itemProduct.get('id')}>
                    <Table.Col product data-title="Sản phẩm">
                      <CartProduct>
                        <Link href={INTERNAL_URI.PRODUCT} as={productUri}>
                          <CardImage className="cart-product__image">
                            <LazyImage
                              className="d-block w-100"
                              alt={itemProduct.get('productName')}
                              src={
                                itemProduct.get('id') === '1'
                                  ? GIFT
                                  : itemProduct.get('image') || ''
                              }
                            />
                          </CardImage>
                        </Link>
                        <CartProduct.Info>
                          <Link href={INTERNAL_URI.PRODUCT} as={productUri}>
                            <a className="cart-product__name">
                              {itemProduct.get('productName')}
                            </a>
                          </Link>
                        </CartProduct.Info>
                      </CartProduct>
                    </Table.Col>
                    <Table.Col price data-title="Giá">
                      {isContact ? (
                        'Liên hệ'
                      ) : (
                        <Product>
                          <Product.Price>
                            <Product.Price.NewPrice>
                              {formatCurrency(
                                parseInt(itemProduct.get('typePrice') || 0)
                              )}
                            </Product.Price.NewPrice>
                          </Product.Price>
                        </Product>
                      )}
                    </Table.Col>
                    <Table.Col quantity data-title="Số lượng">
                      {itemProduct.get('quantity') || 0}
                    </Table.Col>
                    <Table.Col total data-title="Thành tiền">
                      {isContact ? (
                        'Liên hệ'
                      ) : (
                        <Table.Price as="span">
                          {formatCurrency(
                            parseInt(itemProduct.get('totalPrice') || 0)
                          )}
                        </Table.Price>
                      )}
                    </Table.Col>
                  </Table.Row>
                );
              })}
          </Table.Body>
        </TableContainer>
      </ListGroupItem>
    ))}
  </Fragment>
);

export default OrderChecking;
